import React, { useEffect, useState } from 'react';
import LandingHeader from '../../../../components/landing/Header';
import Footer from '../../../../components/landing/Footer';
import { useUserContext } from '../../../protectedRoute/UserContext';
import * as userService from '../../../../api/services/userService';
import { apiClient } from '../../../../api/HttpClient';
import BookingCard from '../../../../components/BookingCard/BookingCard';
import { Eye, EyeOff, CalendarCheck } from 'lucide-react';
import { Button } from '../../../../components/ui/button';

interface ItemDetail {
  type: "bed" | "room" | "property";
  property_id?: string;
  property_name?: string;
  property_address?: string;
  count?: number;
}

interface Booking {
  id: string;
  guest_name: string;
  guest_phone_number: string;
  host_phone_number: string;
  checkin: string;
  checkout: string;
  item_details: ItemDetail[];
  total_price: string;
  status: 'ongoing' | 'completed' | 'cancelled' | 'upcoming';
  booking_id: string;
}

interface CategorizedBookings {
  upcoming: Booking[];
  ongoing: Booking[];
  completed: Booking[];
  cancelled: Booking[];
}

const TABS: { key: keyof CategorizedBookings; label: string }[] = [
  { key: 'upcoming',  label: 'Upcoming'  },
  { key: 'ongoing',   label: 'Ongoing'   },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const GuestDashboard: React.FC = () => {
  const { state } = useUserContext();
  const { user, hostID } = state;

  const [guestOtp, setGuestOtp]   = useState<string | null>(null);
  const [showOtp, setShowOtp]     = useState(false);
  const [bookings, setBookings]   = useState<CategorizedBookings>({ upcoming: [], ongoing: [], completed: [], cancelled: [] });
  const [activeTab, setActiveTab] = useState<keyof CategorizedBookings>('upcoming');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const fetchGuestData = async () => {
      setLoading(true);
      try {
        const otpResponse = await userService.getGuestOtp();
        setGuestOtp(otpResponse.permanent_otp);

        const client = await apiClient;
        const bookingsResponse = await client.get<CategorizedBookings>('/api/guests/bookings/my-bookings/');
        setBookings(bookingsResponse);
      } catch (error) {
        console.error('Failed to fetch guest data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (hostID) {
      fetchGuestData();
    } else {
      setLoading(false);
    }
  }, [hostID]);

  const filteredBookings = bookings[activeTab] ?? [];
  const initials = user?.first_name ? user.first_name.charAt(0).toUpperCase() : 'G';
  const fullName  = user ? `${user.first_name} ${user.last_name}`.trim() : 'Guest';

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <LandingHeader />

      <main className="flex-1 container mx-auto px-4 py-8 pt-24 md:pt-28">
        <div className="flex flex-col md:flex-row gap-6">

          {/* Sidebar */}
          <aside className="w-full md:w-72 shrink-0">
            <div className="bg-card border border-border rounded-2xl shadow-sm p-6 space-y-5">
              {/* Avatar + name */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-xl shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-foreground truncate">{fullName}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email ?? ''}</p>
                </div>
              </div>

              {/* Active nav item */}
              <div className="flex items-center gap-3 bg-primary/10 text-primary rounded-xl px-4 py-2.5 font-semibold text-sm">
                <CalendarCheck size={16} />
                My Bookings
              </div>

              {/* OTP section */}
              <div className="bg-secondary/40 rounded-xl p-4 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Check-in OTP</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full rounded-xl"
                  onClick={() => setShowOtp(v => !v)}
                >
                  {showOtp ? <><EyeOff size={14} className="mr-1.5" /> Hide OTP</> : <><Eye size={14} className="mr-1.5" /> Show OTP</>}
                </Button>
                {showOtp && guestOtp && (
                  <p className="text-center text-2xl font-bold tracking-widest text-primary pt-1">{guestOtp}</p>
                )}
                {showOtp && !guestOtp && (
                  <p className="text-center text-sm text-muted-foreground">No OTP available</p>
                )}
              </div>
            </div>
          </aside>

          {/* Main content */}
          <section className="flex-1 min-w-0">
            <div className="bg-card border border-border rounded-2xl shadow-sm p-6">
              <h1 className="text-2xl font-bold text-foreground mb-5">My Bookings</h1>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-border mb-6">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    {tab.label}
                    {bookings[tab.key].length > 0 && (
                      <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                        activeTab === tab.key ? 'bg-primary/15 text-primary' : 'bg-secondary text-muted-foreground'
                      }`}>
                        {bookings[tab.key].length}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Content */}
              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm">Loading bookings…</p>
                </div>
              ) : filteredBookings.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {filteredBookings.map(booking => (
                    <BookingCard key={booking.id} booking={booking} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 gap-2 text-muted-foreground">
                  <CalendarCheck size={40} className="opacity-30" />
                  <p className="font-medium">No {activeTab} bookings</p>
                  <p className="text-sm opacity-70">Your {activeTab} stays will appear here.</p>
                </div>
              )}
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default GuestDashboard;
